// Type-to-reply input. Submits on Enter; ⌘/Ctrl-Enter forces send even when
// inside an IME composition.

import { useState } from 'react';
import { Send } from 'lucide-react';
import { C } from '@/styles/tokens';

interface TypingReplyProps {
  onSend: (text: string) => void;
  accent: string;
  disabled?: boolean;
}

export function TypingReply({ onSend, accent, disabled = false }: TypingReplyProps) {
  const [text, setText] = useState('');
  const [composing, setComposing] = useState(false);

  function submit() {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText('');
  }

  return (
    <form
      className="flex items-end gap-2 px-4 py-3 safe-bottom"
      style={{ background: C.cream, borderTop: `1px solid ${C.blush}` }}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label className="sr-only" htmlFor="reply-input">
        Type your reply in Chinese
      </label>
      <textarea
        id="reply-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onCompositionStart={() => setComposing(true)}
        onCompositionEnd={() => setComposing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !composing) {
            e.preventDefault();
            submit();
          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
        rows={1}
        placeholder="写一句中文…"
        className="flex-1 resize-none rounded-2xl px-4 py-2.5 ff-zh-serif text-[16px] outline-none transition-colors focus:border-vermillion"
        style={{ background: C.cream, color: C.ink, border: `1px solid ${C.mist}`, maxHeight: 140 }}
        disabled={disabled}
      />
      <button
        type="submit"
        disabled={disabled || text.trim().length === 0}
        className="w-11 h-11 rounded-full flex items-center justify-center text-white disabled:opacity-40"
        style={{ background: accent }}
        aria-label="Send"
      >
        <Send size={18} aria-hidden />
      </button>
    </form>
  );
}
